import type { ReactNode } from 'react';
import { C, H2, Lead, Note } from '../Doc';

function Q({ q, children }: { q: string; children: ReactNode }) {
  return (
    <details className="group border-b border-[var(--ff-border)] py-3">
      <summary className="cursor-pointer list-none font-medium marker:content-none
                          hover:text-[var(--ff-primary)]">
        <span className="mr-2 inline-block text-[var(--ff-text-faint)]
                         transition-transform group-open:rotate-90">›</span>
        {q}
      </summary>
      <div className="mt-2 max-w-[68ch] pl-5 leading-relaxed text-[var(--ff-text-muted)]">
        {children}
      </div>
    </details>
  );
}

export function Faq() {
  return (
    <>
      <Lead>
        Questions that come up repeatedly, grouped by who is asking. Anything describing
        behaviour here is behaviour in this build — where something is not finished, it says
        so.
      </Lead>

      <H2 id="using">Using the studio</H2>

      <Q q="I changed a published form. Why do respondents still see the old one?">
        Editing a published form edits its draft. The live version is immutable, which is
        what stops a form changing under somebody half-way through answering it. Publish
        again to make your edits live.
      </Q>

      <Q q="What is the difference between a tenant, a company and a workspace?">
        The tenant is your account and the only isolation boundary — billing and data both
        stop there. A company is a tier inside it for separating parts of an organisation,
        and a workspace sits under a company and holds forms. See{' '}
        <strong>Application documentation → How the account is shaped</strong>.
      </Q>

      <Q q="I switched company and nothing changed.">
        Switching should take you to that company's forms immediately. If the list looks
        identical, check that the forms genuinely belong to different companies — the
        Companies screen lists each company's workspaces and their form counts.
      </Q>

      <Q q="Where did the folder rail go?">
        Folders live in the left rail of the application now, not inside the forms page, so
        the folder you are in is visible from every screen. The selection is in the URL, so
        a filtered list can be bookmarked or sent to someone.
      </Q>

      <Q q="Can I filter the list by form type?">
        Yes — the dropdown in the forms header. It is in the URL too (<C>?type=Quiz</C>), and
        combines with the folder, the company and the text filter.
      </Q>

      <Q q="Why does a form show no response chart?">
        The sparkline is drawn only when the last seven days actually contained a response. A
        row of flat bars on every form would look like a chart telling you something while
        telling you nothing — the count beside it already says zero.
      </Q>

      <Q q="Can I delete a folder without losing its forms?">
        Yes. Deleting a folder moves its forms out rather than deleting them, and the
        confirmation says how many will be moved.
      </Q>

      <H2 id="forms">Building and publishing</H2>

      <Q q="Publishing failed with errors I do not understand.">
        The validator blocks on errors and allows warnings. Each error carries a
        machine-readable code and the path it applies to, shown beneath the message —
        that code is the thing to search for.
      </Q>

      <Q q="Why can I not add a columns block inside another one?">
        A container inside a container would give the runtime a grid inside a grid cell with
        nothing deciding its width. The builder refuses it and says so.
      </Q>

      <Q q="My translated form still shows some English.">
        A locale overrides only the strings it names, so anything untranslated falls back to
        English per string rather than failing or showing a key. The Translations screen
        lists every translatable string, including the interface ones.
      </Q>

      <Q q="How do I stop the form type label appearing above the title?">
        Translate <C>ui.typeLabel</C> to an empty string for that form.
      </Q>

      <H2 id="integrating">Embedding a form in your own app</H2>

      <Q q="What is the minimum to get a form onto my page?">
        A container and the script:{' '}
        <C>&lt;div data-formforge="f_…"&gt;&lt;/div&gt;</C> plus{' '}
        <C>&lt;script src="…/embed.js" async&gt;&lt;/script&gt;</C>. The Setup guide has the
        rest — modes, sizing, events and tokens.
      </Q>

      <Q q="Nothing renders where my embed should be.">
        Almost always one of three things: the form is still a draft, the form id is wrong,
        or the page's origin is not on the embed's allowlist. A draft has no published
        version to serve, so there is nothing to render.
      </Q>

      <Q q="It works on localhost but not in production.">
        Add the production origin to the embed's allowed origins. An origin is scheme, host
        and port — <C>https://example.com</C> does not cover <C>https://www.example.com</C>,
        and neither covers your staging domain.
      </Q>

      <Q q="How do I know when someone finished?">
        Listen for <C>formforge:complete</C> on the container. The response is already saved
        by the time it fires, and <C>event.detail.responseId</C> identifies it. For
        server-side work use the <C>response.submitted</C> webhook instead — a browser event
        is not a reliable trigger for anything that must happen.
      </Q>

      <Q q="Can I read the answers from my page as they are typed?">
        No, deliberately. The <C>change</C> event tells you which question changed and
        whether it now has a value, not what was entered. Answers go to FormForge, which
        keeps your page out of scope for whatever the form collects.
      </Q>

      <Q q="How do I pre-fill who the respondent is?">
        Mint a signed token on your server and pass it as <C>data-token</C>. Query
        parameters are attacker-supplied by definition, so anything that must not be forged
        has to arrive in the token.
      </Q>

      <Q q="My prefilled value is ignored.">
        The question is not marked prefillable. It is opt-in per question, so an embed
        cannot silently pre-answer whatever it likes.
      </Q>

      <Q q="The iframe stays tiny, or gets clipped.">
        Auto-height needs <C>data-height="auto"</C> (the default) and nothing else setting a
        fixed height on the iframe. The height is capped at three viewports, so a very long
        form scrolls internally rather than growing without limit.
      </Q>

      <Q q="Does it work in React, Vue or another SPA?">
        Yes. The loader watches the document and mounts containers that appear after it
        loaded, so no extra wiring is needed. <C>window.FormForge.scan()</C> exists if you
        want to force it.
      </Q>

      <Q q="Can I use my own UI instead of the embed?">
        Yes — fetch the definition and post answers yourself. See the Developer
        documentation. You take on rendering, logic display and validation feedback; the
        server still re-evaluates everything at submit.
      </Q>

      <H2 id="api">Working with the API</H2>

      <Q q="Which credential do I use?">
        An API key (<C>ffk_live_…</C> or <C>ffk_test_…</C>) sent as a bearer token, from your
        server. User JWTs are for interactive sessions. Never put a key in front-end code.
      </Q>

      <Q q="My submission is rejected as 'submission_too_fast'.">
        Forms carry a minimum completion time, measured on the server from when the session
        was opened. Open the session, then submit after a realistic delay. This catches most
        people writing their first integration test.
      </Q>

      <Q q="Why do I need an Idempotency-Key?">
        So a retried request cannot create a second response. Send the same key when
        retrying and you get the original result back.
      </Q>

      <Q q="My PUT to the definition wiped settings I did not touch.">
        That endpoint replaces the whole document. Read it, modify what you need, write the
        whole thing back.
      </Q>

      <Q q="Saving a definition fails with a JSON conversion error.">
        A field is the wrong type. <C>schemaVersion</C> is a string — sending{' '}
        <C>1</C> instead of <C>"1.0"</C> produces exactly this, and it is reported as a
        malformed body rather than a friendly field message.
      </Q>

      <Q q="How do I verify a webhook is really from FormForge?">
        Recompute the HMAC over <C>{'{timestamp}.{raw body}'}</C> using your endpoint secret
        and compare it with <C>FormForge-Signature</C>, then reject anything outside a
        tolerance window. Verify against the raw bytes, before parsing. There is working
        code in the Developer documentation.
      </Q>

      <Q q="I received the same webhook twice.">
        Delivery is at-least-once, so that is expected. Deduplicate on{' '}
        <C>FormForge-Delivery</C>.
      </Q>

      <Q q="Which id do I use in API calls?">
        The public id returned as <C>id</C> — <C>f_…</C> for forms, <C>co_…</C> for
        companies. Database row ids are never exposed.
      </Q>

      <H2 id="running">Running FormForge itself</H2>

      <Q q="The studio loads but every screen is empty.">
        The dev server proxies to the API on port 5080. If the API is on another port, or not
        running, the studio renders its shell and every request fails. Check{' '}
        <C>http://localhost:5080/health</C>.
      </Q>

      <Q q="A build fails saying a file is locked by FormForge.Api.">
        The API is running from the output folder being written to. Stop it first, or build
        to a different output path.
      </Q>

      <Q q="Everyone was signed out after I restarted the API.">
        In Development the JWT signing key is generated per run when unset, so restarting
        invalidates every token. That is deliberate — it means no key is ever committed.
        Configure <C>Jwt:SigningKey</C> to keep sessions across restarts.
      </Q>

      <Q q="Are uploaded files scanned for viruses?">
        No. Files carry a scan status column and are marked clean, but nothing has looked at
        them. This is listed among the silent gaps in <C>docs/STATUS.md</C>.
      </Q>

      <Q q="Is the response limit exact?">
        No — it is read from sharded counters, so it is approximate under concurrency and a
        burst can overshoot by a few.
      </Q>

      <Note tone="warn" title="Before you rely on a feature">
        <C>docs/STATUS.md</C> keeps a "silent gaps" section: things whose schema, settings or
        UI exist and imply the feature works, where the behaviour behind it does not yet.
        It is the most useful page in the repository and it is maintained by hand.
      </Note>
    </>
  );
}
